import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, Gavel, TrendingUp, Phone, User, AlertCircle, CheckCircle, X, Share2, Eye, ChevronLeft, ChevronRight, Search, Filter } from 'lucide-react';
import { supabase } from '../admin/lib/supabaseClient';
import toast from 'react-hot-toast';
import { trackConversion, trackFunnelStep } from '../utils/analytics';

interface AuctionItem {
  id: string;
  title: string;
  description: string;
  starting_price: number;
  current_price: number;
  reserve_price: number | null;
  image_urls: string[];
  image_url: string | null;
  category: string;
  condition: string;
  start_date: string;
  end_date: string;
  status: string;
  bid_count: number;
  is_featured: boolean;
}

interface BidderInfo {
  id: string;
  name: string;
  phone: string;
  email?: string;
  session_id: string;
}

const AuctionsPage: React.FC = () => {
  const [auctionItems, setAuctionItems] = useState<AuctionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<AuctionItem | null>(null);
  const [showBidModal, setShowBidModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [bidAmount, setBidAmount] = useState('');
  const [bidderInfo, setBidderInfo] = useState<BidderInfo | null>(null);
  const [registerForm, setRegisterForm] = useState({ name: '', phone: '', email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsExpanded, setTermsExpanded] = useState(false);
  const [pageTermsExpanded, setPageTermsExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('ending-soon');
  const [myBids, setMyBids] = useState<Array<{
    auction_item_id: string;
    bid_amount: number;
    is_winning_bid: boolean;
    created_at: string;
  }>>([]);

  // Normalize phone number to international format (South Africa)
  const normalizePhoneNumber = (phone: string): string => {
    // Remove all non-digit characters
    let cleaned = phone.replace(/\D/g, '');
    
    // If it starts with 0, replace with 27 (South Africa)
    if (cleaned.startsWith('0')) {
      cleaned = '27' + cleaned.substring(1);
    }
    
    // If it doesn't start with country code and is 9 digits, add 27
    if (!cleaned.startsWith('27') && cleaned.length === 9) {
      cleaned = '27' + cleaned;
    }
    
    // If it starts with 27 already, keep as is
    return cleaned;
  };

  useEffect(() => {
    loadAuctions();
    loadBidderInfo();
    
    // Check if URL has an item ID to scroll to
    const urlParams = new URLSearchParams(window.location.search);
    const itemId = urlParams.get('item');
    if (itemId) {
      setTimeout(() => {
        const element = document.getElementById(`auction-${itemId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.classList.add('ring-4', 'ring-mini-red', 'ring-opacity-50');
          setTimeout(() => {
            element.classList.remove('ring-4', 'ring-mini-red', 'ring-opacity-50');
          }, 3000);
        }
      }, 500);
    }
    
    // Set up real-time subscriptions for auction updates
    const channel = supabase
      .channel('auction_updates')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'auction_bids' 
      }, () => {
        loadAuctions(); // Reload when new bids are placed
        if (bidderInfo) {
          loadMyBids(bidderInfo.id); // Reload user's bids
        }
      })
      .on('postgres_changes', { 
        event: 'UPDATE', 
        schema: 'public', 
        table: 'auction_items' 
      }, () => {
        loadAuctions(); // Reload when auction items are updated (price, winner, etc)
      })
      .subscribe();

    // Polling fallback every 15 seconds to ensure data stays fresh
    const pollInterval = setInterval(() => {
      loadAuctions();
    }, 15000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(pollInterval);
    };
  }, []);

  const loadAuctions = async () => {
    try {
      // Load active, upcoming auctions AND sold items where show_as_sold = true
      const { data, error } = await supabase
        .from('auction_items')
        .select('*')
        .or('status.in.(upcoming,active),and(status.eq.sold,show_as_sold.eq.true)')
        .order('updated_at', { ascending: false })  // Order by updated_at to get freshest first
        .order('end_date', { ascending: true });

      if (error) throw error;
      
      // Force state update even if data looks the same
      setAuctionItems([...(data || [])]);
      
      // Update selected item if modal is open and item exists in new data
      if (selectedItem && data) {
        const updatedItem = data.find(item => item.id === selectedItem.id);
        if (updatedItem) {
          // Force update with fresh data
          setSelectedItem({...updatedItem});
        }
      }
    } catch (error) {
      toast.error('Failed to load auctions');
    } finally {
      setLoading(false);
    }
  };

  const loadBidderInfo = async () => {
    const stored = sessionStorage.getItem('bidderInfo');
    if (stored) {
      const info = JSON.parse(stored);
      setBidderInfo(info);
      loadMyBids(info.id);
    }
  };

  const loadMyBids = async (bidderId: string) => {
    try {
      const { data, error } = await supabase
        .from('auction_bids')
        .select('auction_item_id, bid_amount, is_winning_bid, created_at')
        .eq('bidder_id', bidderId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setMyBids(data || []);
    } catch (error) {
      console.error('Error loading bids:', error);
    }
  };

  const saveBidderInfo = (info: BidderInfo) => {
    sessionStorage.setItem('bidderInfo', JSON.stringify(info));
    setBidderInfo(info);
    loadMyBids(info.id);
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!registerForm.name || !registerForm.phone) {
      toast.error('Please provide your name and phone number');
      return;
    }

    if (!termsAccepted) {
      toast.error('Please accept the Terms & Conditions to continue');
      return;
    }

    setSubmitting(true);
    
    // Normalize phone number to international format
    const normalizedPhone = normalizePhoneNumber(registerForm.phone);
    
    // Validate phone number (should be 11 digits for SA: 27XXXXXXXXX)
    if (normalizedPhone.length < 11 || !normalizedPhone.startsWith('27')) {
      toast.error('Please enter a valid South African phone number');
      setSubmitting(false);
      return;
    }
    
    // Track registration attempt
    trackFunnelStep('auction_bid', 'registration_started', { 
      auctionItemId: selectedItem?.id 
    });

    try {
      // Generate or get session ID
      let sessionId = sessionStorage.getItem('sessionId');
      if (!sessionId) {
        sessionId = crypto.randomUUID();
        sessionStorage.setItem('sessionId', sessionId);
      }

      // Check if bidder already exists by normalized phone
      const { data: existingBidder, error: checkError } = await supabase
        .from('auction_bidders')
        .select('*')
        .eq('phone', normalizedPhone)
        .maybeSingle();

      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }

      let bidderId: string;

      if (existingBidder) {
        // Update existing bidder's session
        const { data: updated, error: updateError } = await supabase
          .from('auction_bidders')
          .update({ 
            session_id: sessionId,
            name: registerForm.name,
            email: registerForm.email || null
          })
          .eq('id', existingBidder.id)
          .select()
          .single();

        if (updateError) throw updateError;
        bidderId = updated.id;
      } else {
        // Create new bidder with normalized phone
        const { data: newBidder, error: insertError } = await supabase
          .from('auction_bidders')
          .insert([{
            name: registerForm.name,
            phone: normalizedPhone,
            email: registerForm.email || null,
            session_id: sessionId
          }])
          .select()
          .single();

        if (insertError) throw insertError;
        bidderId = newBidder.id;
      }

      const bidderInfo: BidderInfo = {
        id: bidderId,
        name: registerForm.name,
        phone: normalizedPhone,
        email: registerForm.email,
        session_id: sessionId
      };

      saveBidderInfo(bidderInfo);
      setShowRegisterModal(false);
      
      // Track successful registration
      trackConversion('auction_registration', undefined, { 
        bidderId: bidderId,
        isNewBidder: !existingBidder 
      });
      trackFunnelStep('auction_bid', 'registration_completed', { 
        bidderId: bidderId 
      });
      
      toast.success('Registration successful! You can now place bids.');
      
      // If there's a selected item, verify it still exists and show bid modal
      if (selectedItem) {
        // Verify the auction item still exists and is active
        const { data: verifyAuction, error: verifyError } = await supabase
          .from('auction_items')
          .select('*')
          .eq('id', selectedItem.id)
          .in('status', ['active', 'upcoming'])
          .single();
        
        if (verifyError || !verifyAuction) {
          toast.error('The auction you selected is no longer available. Please select another item.');
          setSelectedItem(null);
          await loadAuctions();
        } else {
          // Update selected item with fresh data
          setSelectedItem(verifyAuction);
          setShowBidModal(true);
          setBidAmount(String((verifyAuction.current_price + 2000) / 100)); // Suggest R20 more
        }
      }
    } catch (error: any) {
      trackFunnelStep('auction_bid', 'registration_error', { 
        error: String(error) 
      });
      toast.error('Unable to complete registration. Please try again or contact support.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBidClick = async (item: AuctionItem) => {
    // Verify the auction still exists and is active before proceeding
    const { data: freshAuction, error: verifyError } = await supabase
      .from('auction_items')
      .select('*')
      .eq('id', item.id)
      .single();
    
    if (verifyError || !freshAuction) {
      toast.error('This auction item is no longer available.');
      await loadAuctions(); // Refresh the list
      return;
    }
    
    // Check if auction status allows bidding
    if (freshAuction.status !== 'active') {
      if (freshAuction.status === 'upcoming') {
        toast.error(`Bidding hasn't started yet. Auction starts: ${new Date(freshAuction.start_date).toLocaleString()}`);
      } else {
        toast.error('This auction is no longer active.');
      }
      await loadAuctions();
      return;
    }
    
    // Check if auction has ended
    const now = new Date();
    const end = new Date(freshAuction.end_date);
    if (end <= now) {
      toast.error('This auction has ended.');
      await loadAuctions();
      return;
    }
    
    // Set the fresh auction data
    setSelectedItem(freshAuction);
    
    // Track auction item view
    trackFunnelStep('auction_bid', 'item_viewed', { 
      auctionItemId: freshAuction.id,
      currentPrice: freshAuction.current_price,
      title: freshAuction.title 
    });
    
    if (!bidderInfo) {
      setShowRegisterModal(true);
    } else {
      setShowBidModal(true);
      setBidAmount(String((freshAuction.current_price + 2000) / 100)); // Suggest R20 more
    }
  };

  const handlePlaceBid = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate we have required data before proceeding
    if (!selectedItem) {
      toast.error('Auction item not found. Please refresh the page and try again.');
      setShowBidModal(false);
      await loadAuctions();
      return;
    }
    
    if (!bidderInfo) {
      toast.error('Please register first to place a bid.');
      setShowBidModal(false);
      setShowRegisterModal(true);
      return;
    }

    const bidAmountCents = Math.round(parseFloat(bidAmount) * 100);
    
    const DEBUG = localStorage.getItem('AUCTION_DEBUG') === '1';
    
    // Disable submitting state
    setSubmitting(true);
    
    // Reload latest auction data to ensure we have current price
    // Force fresh data by ordering and using a unique timestamp
    const { data: latestAuction, error: fetchError } = await supabase
      .from('auction_items')
      .select('*')
      .eq('id', selectedItem.id)
      .order('updated_at', { ascending: false })  // Force fresh read
      .limit(1)
      .single();

    if (DEBUG) {
      console.log('[AUCTION DEBUG] latestAuction fetch result:', latestAuction);
      console.log('[AUCTION DEBUG] latestAuction.current_price:', latestAuction?.current_price);
      console.log('[AUCTION DEBUG] selectedItem.current_price (before update):', selectedItem.current_price);
      if (fetchError) {
        console.log('[AUCTION DEBUG] fetchError:', fetchError);
      }
    }

    if (fetchError || !latestAuction) {
      setSubmitting(false);
      
      // Provide more specific error messages
      if (fetchError?.code === 'PGRST116') {
        toast.error('This auction item no longer exists. It may have been removed or ended.');
      } else if (fetchError) {
        console.error('Auction fetch error:', fetchError);
        toast.error(`Unable to load auction: ${fetchError.message}`);
      } else {
        toast.error('Auction item not found. Please refresh and try again.');
      }
      
      setShowBidModal(false);
      await loadAuctions(); // Reload all auctions
      return;
    }

    if (latestAuction) {
      setSelectedItem(latestAuction);
      // Block bids unless auction is active and not ended
      // Use UTC for consistent timezone comparison
      const now = new Date();
      const end = new Date(latestAuction.end_date);
      // Ensure we're comparing in the same timezone (both will be interpreted as UTC if end_date is ISO)
      const nowUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 
                              now.getUTCHours(), now.getUTCMinutes(), now.getUTCSeconds());
      const endUTC = end.getTime();
      
      if (latestAuction.status !== 'active') {
        toast.error(
          latestAuction.status === 'upcoming'
            ? `Bidding hasn't opened yet. Starts: ${new Date(latestAuction.start_date).toLocaleString()}`
            : `This auction is not active (status: ${latestAuction.status}).`
        );
        return;
      }
      if (endUTC <= nowUTC) {
        toast.error(`This auction has ended (${end.toLocaleString()}).`);
        return;
      }
      
      // Re-validate against latest price
      if (DEBUG) {
        console.log('[AUCTION DEBUG] Validation checks:');
        console.log('  bidAmountCents:', bidAmountCents);
        console.log('  latestAuction.current_price:', latestAuction.current_price);
        console.log('  bidAmountCents > current_price?', bidAmountCents > latestAuction.current_price);
      }
      
      if (bidAmountCents <= latestAuction.current_price) {
        if (DEBUG) console.log('[AUCTION DEBUG] FAILED: Bid not higher than current price');
        toast.error(`Bid must be higher than current bid of R${(latestAuction.current_price / 100).toFixed(2)}. Someone else may have bid.`);
        return;
      }

      // Ensure bid is at least R20 more than current price (use latestAuction, not stale selectedItem)
      const minimumBidCents = latestAuction.current_price + 2000;
      
      // Enforce R20 increments from current price (not absolute R20 multiples)
      const incrementFromCurrent = bidAmountCents - latestAuction.current_price;
      if (DEBUG) {
        console.log('[AUCTION DEBUG] Increment check:');
        console.log('  bidAmountCents:', bidAmountCents);
        console.log('  current_price:', latestAuction.current_price);
        console.log('  incrementFromCurrent:', incrementFromCurrent);
        console.log('  incrementFromCurrent % 2000:', incrementFromCurrent % 2000);
      }
      
      if (incrementFromCurrent % 2000 !== 0) {
        if (DEBUG) console.log('[AUCTION DEBUG] FAILED: Not in R20 increments from current price');
        toast.error('Bids must be in R20 increments above the current price');
        return;
      }
      if (DEBUG) {
        console.log('[AUCTION DEBUG] Minimum bid check:');
        console.log('  minimumBidCents:', minimumBidCents);
        console.log('  bidAmountCents >= minimumBidCents?', bidAmountCents >= minimumBidCents);
      }
      
      if (bidAmountCents < minimumBidCents) {
        if (DEBUG) console.log('[AUCTION DEBUG] FAILED: Below minimum bid');
        toast.error(`Minimum bid is R${(minimumBidCents / 100).toFixed(2)}`);
        return;
      }
      
      if (DEBUG) console.log('[AUCTION DEBUG] ✅ All validations passed! Proceeding to insert...');
    } else {
      toast.error('Could not load latest auction data. Please try again.');
      return;
    }

    setSubmitting(true);
    
    // Track bid attempt
    trackFunnelStep('auction_bid', 'bid_submitted', { 
      auctionItemId: selectedItem.id,
      bidAmount: bidAmountCents 
    });

    try {
      const DEBUG = localStorage.getItem('AUCTION_DEBUG') === '1';
      if (DEBUG) {
        console.groupCollapsed('[AUCTION DEBUG] Bid submit payload');
        console.log('auction_item_id:', selectedItem.id);
        console.log('bidder_id:', bidderInfo.id);
        console.log('bid_amount_cents:', bidAmountCents);
        console.log('selectedItem.current_price:', selectedItem.current_price);
        console.log('bidAmountCents:', bidAmountCents);
        console.log('selectedItem snapshot:', selectedItem);
        console.log('bidderInfo snapshot:', bidderInfo);
        console.groupEnd();
      }

      const { error } = await supabase
        .from('auction_bids')
        .insert({
          auction_item_id: selectedItem.id,
          bidder_id: bidderInfo.id,
          bid_amount: bidAmountCents
        });

      if (DEBUG) {
        console.groupCollapsed('[AUCTION DEBUG] Insert result');
        console.log('error:', error);
        if (error) {
          console.log('ERROR DETAILS:');
          console.log('  message:', error.message);
          console.log('  code:', error.code);
          console.log('  details:', error.details);
          console.log('  hint:', error.hint);
        }
        console.groupEnd();
      }

      if (error) {
        // Check if error is due to bid validation or concurrent bid
        if (error.message?.includes('must be at least') || error.message?.includes('no longer higher')) {
          // Someone else bid! Show clear message
          toast.error('⚠️ Someone else just placed a bid! The price has increased.');
          
          // Wait a moment for database to fully commit, then reload
          await new Promise(resolve => setTimeout(resolve, 300));
          await loadAuctions();
          
          setBidAmount(''); // Clear the bid input
          setSubmitting(false); // Re-enable submit button
          setShowBidModal(false); // Close the modal
          
          // Wait then reopen modal with fresh data
          setTimeout(async () => {
            // Fetch fresh data directly
            const { data: freshAuction } = await supabase
              .from('auction_items')
              .select('*')
              .eq('id', selectedItem.id)
              .single();
              
            if (freshAuction) {
              setSelectedItem(freshAuction);
              setShowBidModal(true);
              toast(`💰 Current price is now R${(freshAuction.current_price / 100).toFixed(2)}`, {
                icon: 'ℹ️',
                duration: 4000
              });
            }
          }, 800);
        } else {
          setSubmitting(false);
          throw error;
        }
        return;
      }

      // Track successful bid
      trackConversion('auction_bid', bidAmountCents / 100, { 
        auctionItemId: selectedItem.id,
        bidAmount: bidAmountCents,
        bidderId: bidderInfo.id 
      });
      trackFunnelStep('auction_bid', 'bid_placed_successfully', { 
        auctionItemId: selectedItem.id,
        bidAmount: bidAmountCents 
      });

      toast.success('Bid placed successfully!');
      setShowBidModal(false);
      setBidAmount('');
      setSubmitting(false);
      
      // Immediate reload to show updated price
      await loadAuctions();
    } catch (error: any) {
      const DEBUG = localStorage.getItem('AUCTION_DEBUG') === '1';
      trackFunnelStep('auction_bid', 'bid_error', { 
        error: String(error),
        auctionItemId: selectedItem.id 
      });
      if (DEBUG) {
        console.group('[AUCTION DEBUG] Error details');
        console.log('message:', error?.message);
        console.log('code:', error?.code);
        console.log('details:', error?.details);
        console.log('hint:', error?.hint);
        console.log('stack:', error?.stack);
        console.groupEnd();
      }
      // Show specific error message if available
      const errorMessage = error?.message || 'Failed to place bid. Please try again.';
      toast.error(errorMessage);
      setSubmitting(false);
      // Reload auction data in case of any error
      await loadAuctions();
    } finally {
      // Ensure submitting is always reset
      setSubmitting(false);
    }
  };

  const handleShare = async (item: AuctionItem) => {
    const shareUrl = `${window.location.origin}/auctions?item=${item.id}`;
    const shareText = `Check out this auction: ${item.title} - Current bid: R${(item.current_price / 100).toFixed(2)}`;

    // Try native share API first (mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: item.title,
          text: shareText,
          url: shareUrl
        });
        toast.success('Shared successfully!');
      } catch (error) {
        // User cancelled share, no action needed
      }
    } else {
      // Fallback: Copy to clipboard
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
      } catch (error) {
        toast.error('Failed to copy link');
      }
    }
  };

  const handleViewDetails = (item: AuctionItem) => {
    setSelectedItem(item);
    setCurrentImageIndex(0);
    setShowDetailsModal(true);
  };

  const nextImage = () => {
    if (selectedItem && selectedItem.image_urls && selectedItem.image_urls.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % selectedItem.image_urls.length);
    }
  };

  const prevImage = () => {
    if (selectedItem && selectedItem.image_urls && selectedItem.image_urls.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + selectedItem.image_urls.length) % selectedItem.image_urls.length);
    }
  };

  const formatCategoryName = (category: string) => {
    const categoryMap: { [key: string]: string } = {
      'car': 'Complete Car',
      'body-trimmings': 'Body Trimmings',
      'badges': 'Badges',
      'glass': 'Glass',
      'wheels': 'Wheels',
      'interior-upholstery': 'Interior Upholstery',
      'engine': 'Engine',
      'parts': 'Parts',
      'accessories': 'Accessories',
      'accessory': 'Accessories',
      'collectible': 'Collectible',
      'other': 'Other'
    };
    return categoryMap[category] || category.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const normalizeCategoryKey = (category?: string) => {
    if (!category) return '';
    return category.trim().toLowerCase().replace(/\s+/g, '-');
  };

  const getTimeRemaining = (endDate: string) => {
    const now = new Date();
    const end = new Date(endDate);
    const diff = end.getTime() - now.getTime();

    if (diff <= 0) return 'Ended';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const getStatusBadge = (item: AuctionItem) => {
    // Prefer explicit status if set
    if (item.status === 'sold') {
      return <span className="px-2 py-1 bg-red-600 text-white text-xs rounded-full">SOLD</span>;
    }
    const now = new Date();
    const start = new Date(item.start_date);
    const end = new Date(item.end_date);

    if (now < start) {
      return <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Upcoming</span>;
    }
    if (now > end) {
      return <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">Ended</span>;
    }
    return <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full animate-pulse">Live</span>;
  };

  if (loading) {
    return (
  <div className="min-h-[60vh] md:min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mini-red mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading auctions...</p>
        </div>
      </div>
    );
  }

  return (
  <div className="min-h-[80vh] md:min-h-screen bg-gradient-to-br from-gray-50 to-white pt-24 pb-16 relative">
      <div className="container-custom px-4">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="inline-flex items-center space-x-2 bg-mini-red/10 text-mini-red px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Gavel size={16} />
            <span>Live Auctions</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-heading font-bold text-gray-900 mb-4">
            Classic Mini Auctions
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Bid on authentic parts, collectibles, and classic Mini accessories. All proceeds support our restoration projects.
          </p>
        </motion.div>

        {/* Filters */}
        <div className="bg-white shadow-sm p-6 mb-8 rounded-lg border">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search auctions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Category Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white appearance-none"
              >
                <option value="all">All Categories</option>
                <optgroup label="Vehicle">
                  <option value="car">Complete Car</option>
                </optgroup>
                <optgroup label="Body & Exterior">
                  <option value="body-trimmings">Body Trimmings</option>
                  <option value="badges">Badges</option>
                  <option value="glass">Glass</option>
                  <option value="wheels">Wheels</option>
                </optgroup>
                <optgroup label="Interior">
                  <option value="interior-upholstery">Interior Upholstery</option>
                </optgroup>
                <optgroup label="Mechanical">
                  <option value="engine">Engine</option>
                  <option value="parts">Other Parts</option>
                </optgroup>
                <optgroup label="Other">
                  <option value="accessories">Accessories</option>
                  <option value="collectible">Collectible</option>
                  <option value="other">Other</option>
                </optgroup>
              </select>
            </div>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="ending-soon">Ending Soon</option>
              <option value="newly-listed">Newly Listed</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="most-bids">Most Bids</option>
            </select>
          </div>
        </div>

        {/* Terms & Conditions Expandable Box */}
        <motion.div
          className="max-w-4xl mx-auto mb-8 bg-blue-50 border border-blue-200 rounded-lg overflow-hidden"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <button
            type="button"
            onClick={() => setPageTermsExpanded(!pageTermsExpanded)}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-blue-100 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <AlertCircle className="text-blue-600" size={20} />
              <span className="font-semibold text-blue-900">📋 Auction Terms & Conditions</span>
            </div>
            <span className="text-blue-600 text-xl font-bold">{pageTermsExpanded ? '−' : '+'}</span>
          </button>
          
          {pageTermsExpanded && (
            <div className="px-6 pb-6 pt-2 text-sm text-blue-900 space-y-3 border-t border-blue-200">
              <p className="font-medium">Please read these important terms before bidding:</p>
              
              <div>
                <p className="font-semibold">⏰ Payment Deadline:</p>
                <p>Items that have been bid on and won must be paid for within 48 hours of the auction's end.</p>
              </div>

              <div>
                <p className="font-semibold">💳 Payment Methods:</p>
                <p>Bank transfer only. Banking details will be provided to the winner via WhatsApp or email.</p>
              </div>

              <div>
                <p className="font-semibold">📦 Collection & Delivery:</p>
                <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                  <li>Free collection from our premises (by appointment)</li>
                  <li>PUDO delivery available on request (delivery cost invoiced separately)</li>
                  <li>Items must be collected/shipped within 7 days of confirmed payment</li>
                </ul>
              </div>

              <div>
                <p className="font-semibold">🔨 Bidding Rules:</p>
                <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                  <li>Minimum bid increment: R20</li>
                  <li>All bids are binding and cannot be retracted</li>
                  <li>Anti-sniping: Auctions extend by 5 minutes if a bid is placed in the final 5 minutes</li>
                </ul>
              </div>

              <div>
                <p className="font-semibold">📞 Contact:</p>
                <p>Email: mike@silvertoncc.co.za | Phone: 072 998 1365</p>
              </div>

              <div className="pt-2 border-t border-blue-300">
                <p className="text-xs text-blue-700">By placing a bid, you agree to these terms and conditions.</p>
              </div>
            </div>
          )}
        </motion.div>

        {/* My Bids Section */}
        {bidderInfo && myBids.length > 0 && (
          <motion.div
            className="max-w-4xl mx-auto mb-8 bg-white border border-gray-200 rounded-lg p-6 shadow-sm"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Gavel className="mr-2" size={20} />
              My Active Bids
            </h3>
            <div className="space-y-3">
              {myBids.map((bid) => {
                const auction = auctionItems.find(item => item.id === bid.auction_item_id);
                if (!auction) return null;
                
                return (
                  <div
                    key={bid.auction_item_id}
                    className={`p-4 rounded-lg border-2 transition-all cursor-pointer hover:shadow-md ${
                      bid.is_winning_bid 
                        ? 'bg-green-50 border-green-300' 
                        : 'bg-orange-50 border-orange-300'
                    }`}
                    onClick={() => {
                      const element = document.getElementById(`auction-${auction.id}`);
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        element.classList.add('ring-4', 'ring-mini-red', 'ring-opacity-50');
                        setTimeout(() => {
                          element.classList.remove('ring-4', 'ring-mini-red', 'ring-opacity-50');
                        }, 2000);
                      }
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          {bid.is_winning_bid ? (
                            <CheckCircle className="text-green-600 flex-shrink-0" size={18} />
                          ) : (
                            <AlertCircle className="text-orange-600 flex-shrink-0" size={18} />
                          )}
                          <h4 className="font-semibold text-gray-900">{auction.title}</h4>
                        </div>
                        <div className="flex items-center space-x-4 text-sm">
                          <span className={bid.is_winning_bid ? 'text-green-700' : 'text-orange-700'}>
                            Your bid: <span className="font-semibold">R{(bid.bid_amount / 100).toFixed(2)}</span>
                          </span>
                          {!bid.is_winning_bid && (
                            <span className="text-orange-700">
                              Current bid: <span className="font-semibold">R{(auction.current_price / 100).toFixed(2)}</span>
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        {bid.is_winning_bid ? (
                          <span className="inline-flex items-center px-3 py-1 bg-green-600 text-white text-xs font-semibold rounded-full">
                            ✓ Winning
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-3 py-1 bg-orange-600 text-white text-xs font-semibold rounded-full">
                            ⚠ Outbid
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Bidder Info Banner */}
        {bidderInfo && (
          <motion.div
            className="max-w-2xl mx-auto mb-8 bg-green-50 border border-green-200 rounded-lg p-4"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <CheckCircle className="text-green-600" size={20} />
                <div>
                  <p className="text-sm font-medium text-green-900">
                    Registered as: {bidderInfo.name}
                  </p>
                  <p className="text-xs text-green-700">
                    Phone: {bidderInfo.phone}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  sessionStorage.removeItem('bidderInfo');
                  setBidderInfo(null);
                  toast.success('Registration cleared');
                }}
                className="text-green-600 hover:text-green-800 text-sm"
              >
                Change
              </button>
            </div>
          </motion.div>
        )}

        {/* Auction Items Grid */}
        {(() => {
          // Apply search and category filters
          let filteredItems = auctionItems.filter(item => {
            // Search filter
            const matchesSearch = !searchTerm.trim() || (() => {
              const q = searchTerm.toLowerCase();
              return (
                item.title.toLowerCase().includes(q) ||
                (item.description || '').toLowerCase().includes(q) ||
                (item.category || '').toLowerCase().includes(q) ||
                (item.condition || '').toLowerCase().includes(q)
              );
            })();
            
            // Category filter (normalize both sides so DB values like "Badges" or "badges" still match)
            const matchesCategory = selectedCategory === 'all' || normalizeCategoryKey(item.category) === normalizeCategoryKey(selectedCategory);
            
            return matchesSearch && matchesCategory;
          });

          // Apply sorting
          filteredItems.sort((a, b) => {
            switch (sortBy) {
              case 'ending-soon':
                return new Date(a.end_date).getTime() - new Date(b.end_date).getTime();
              case 'newly-listed':
                return new Date(b.start_date).getTime() - new Date(a.start_date).getTime();
              case 'price-low':
                return a.current_price - b.current_price;
              case 'price-high':
                return b.current_price - a.current_price;
              case 'most-bids':
                return (b.bid_count || 0) - (a.bid_count || 0);
              default:
                return 0;
            }
          });

          if (filteredItems.length === 0) {
            return (
              <div className="text-center py-16">
                <Gavel size={48} className="mx-auto text-gray-400 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {searchTerm ? 'No auctions found' : 'No Active Auctions'}
                </h3>
                <p className="text-gray-600">
                  {searchTerm ? 'Try a different search term' : 'Check back soon for new items!'}
                </p>
              </div>
            );
          }

          return (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredItems.map((item, index) => (
              <motion.div
                key={`${item.id}-${item.current_price}-${item.bid_count || 0}`}
                id={`auction-${item.id}`}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                {/* Item Image */}
                <div className="relative h-48 bg-gray-200">
                  {item.image_url || item.image_urls?.[0] ? (
                    <img
                      src={item.image_url || item.image_urls[0]}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Gavel size={48} className="text-gray-400" />
                    </div>
                  )}
                  {/* Red SOLD ribbon */}
                  {item.status === 'sold' && (
                    <div className="absolute left-0 top-3 -translate-y-0 -rotate-6 origin-left">
                      <span className="bg-red-600 text-white px-3 py-1 text-xs font-bold rounded shadow-lg">SOLD</span>
                    </div>
                  )}
                  <div className="absolute top-2 right-2">
                    {getStatusBadge(item)}
                  </div>
                  <div className="absolute top-2 left-2 flex flex-col gap-2">
                    {item.is_featured && (
                      <span className="px-2 py-1 bg-yellow-400 text-yellow-900 text-xs rounded-full font-bold">
                        Featured
                      </span>
                    )}
                    {(() => {
                      const myBid = myBids.find(bid => bid.auction_item_id === item.id);
                      if (myBid) {
                        return myBid.is_winning_bid ? (
                          <span className="px-2 py-1 bg-green-500 text-white text-xs rounded-full font-bold flex items-center">
                            <CheckCircle size={12} className="mr-1" />
                            You're Winning!
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-orange-500 text-white text-xs rounded-full font-bold flex items-center">
                            <AlertCircle size={12} className="mr-1" />
                            Outbid
                          </span>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>

                {/* Item Details */}
                <div className="p-4">
                  <h3 
                    className="text-lg font-semibold text-gray-900 mb-2 cursor-pointer hover:text-mini-red transition-colors"
                    onClick={() => handleViewDetails(item)}
                  >
                    {item.title}
                  </h3>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {formatCategoryName(item.category)}
                    </span>
                    <span className="text-xs text-gray-500">•</span>
                    <span className="text-xs text-gray-500 capitalize">
                      {item.condition?.replace(/-/g, ' ')}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {item.description}
                  </p>

                  {/* Pricing Info */}
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">{item.status === 'sold' ? 'Sold For:' : 'Current Bid:'}</span>
                      <span className="text-xl font-bold text-mini-red">
                        R{(item.current_price / 100).toFixed(2)}
                      </span>
                    </div>
                    {item.status !== 'sold' && (
                      <div className="flex justify-between items-center text-xs text-gray-500">
                        <span>Starting: R{(item.starting_price / 100).toFixed(2)}</span>
                        <span className="flex items-center">
                          <TrendingUp size={12} className="mr-1" />
                          {item.bid_count || 0} bids
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Time Remaining */}
                  <div className="flex items-center justify-between mb-4 text-sm">
                    <div className="flex items-center text-gray-600">
                      <Clock size={14} className="mr-1" />
                      <span>Ends in: {getTimeRemaining(item.end_date)}</span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-2">
                    <button
                      onClick={() => handleBidClick(item)}
                      disabled={item.status !== 'active'}
                      className="w-full bg-mini-red text-white py-2 rounded-lg font-medium hover:bg-red-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      {item.status === 'active' ? 'Place Bid' : 'Auction Ended'}
                    </button>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handleViewDetails(item)}
                        className="flex-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-1"
                        title="View full details"
                      >
                        <Eye size={16} />
                        <span className="text-sm">Details</span>
                      </button>
                      <button
                        onClick={() => handleShare(item)}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center"
                        title="Share this auction"
                      >
                        <Share2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* Register Modal */}
      {showRegisterModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <motion.div
            className="bg-white rounded-xl max-w-md w-full p-6"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Register to Bid</h3>
              <button
                onClick={() => setShowRegisterModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <AlertCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <p className="text-sm text-blue-900">
                  We'll contact you via phone if you win. Your information is kept confidential.
                </p>
              </div>
            </div>

            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <User size={14} className="inline mr-1" />
                  Your Name *
                </label>
                <input
                  type="text"
                  required
                  value={registerForm.name}
                  onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Phone size={14} className="inline mr-1" />
                  Phone Number *
                </label>
                <input
                  type="tel"
                  required
                  value={registerForm.phone}
                  onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent"
                  placeholder="0721234567 or 072 123 4567"
                />
                {registerForm.phone && registerForm.phone.replace(/\D/g, '').length >= 9 && (
                  <p className="text-xs text-green-600 mt-1 flex items-center">
                    <CheckCircle size={12} className="mr-1" />
                    Will be saved as: +{normalizePhoneNumber(registerForm.phone)}
                  </p>
                )}
                {registerForm.phone && registerForm.phone.replace(/\D/g, '').length < 9 && (
                  <p className="text-xs text-orange-600 mt-1">
                    Please enter at least 9 digits
                  </p>
                )}
                {!registerForm.phone && (
                  <p className="text-xs text-gray-500 mt-1">
                    Local format will be auto-converted (0659926112 → +27659926112)
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email (Optional)
                </label>
                <input
                  type="email"
                  value={registerForm.email}
                  onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent"
                  placeholder="john@example.com"
                />
              </div>

              {/* Terms & Conditions */}
              <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                <button
                  type="button"
                  onClick={() => setTermsExpanded(!termsExpanded)}
                  className="w-full text-left flex items-center justify-between mb-2"
                >
                  <span className="font-semibold text-gray-900 text-sm">
                    📢 Important Auction Terms & Conditions
                  </span>
                  <span className="text-mini-red">{termsExpanded ? '−' : '+'}</span>
                </button>
                
                {termsExpanded && (
                  <div className="text-sm text-gray-700 space-y-3 mb-3 max-h-60 overflow-y-auto pr-2">
                    <p className="font-medium text-gray-900">Please read the following essential terms carefully. Your participation signifies agreement to these rules.</p>
                    
                    <div>
                      <p className="font-semibold text-gray-900">⏰ Payment Deadline:</p>
                      <p>Items that have been bid on and won must be paid for within 48 hours of the auction's end.</p>
                    </div>
                    
                    <div>
                      <p className="font-semibold text-gray-900">📦 Postage & Courier:</p>
                      <p>Shipping services are available on selected items and will be costed and invoiced separately from the winning bid.</p>
                    </div>
                    
                    <div>
                      <p className="font-semibold text-gray-900">❌ Non-Payment Policy:</p>
                      <p>If payment is not received within the 48-hour window, the item will be automatically offered to the runner-up bidder.</p>
                    </div>
                    
                    <div>
                      <p className="font-semibold text-gray-900">📞 Contact Information & Queries:</p>
                      <p>If you require more information about any item, you are welcome to message us for assistance.</p>
                      <p className="mt-1"><strong>Email:</strong> mike@silvertoncc.co.za</p>
                      <p><strong>Phone:</strong> 072 998 1365</p>
                    </div>
                    
                    <p className="text-center font-medium text-mini-red mt-4">Thank you for your incredible support! Happy Bidding & Good Luck! 🍀</p>
                  </div>
                )}

                <label className="flex items-start space-x-2 cursor-pointer mt-3">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="mt-1 w-4 h-4 text-mini-red border-gray-300 rounded focus:ring-mini-red"
                  />
                  <span className="text-sm text-gray-700">
                    I have read and agree to the <button type="button" onClick={() => setTermsExpanded(true)} className="text-mini-red hover:underline font-medium">Terms & Conditions</button>
                  </span>
                </label>
              </div>

              <button
                type="submit"
                disabled={!termsAccepted}
                className="w-full bg-mini-red text-white py-2 rounded-lg font-medium hover:bg-red-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Register & Continue
              </button>
            </form>
          </motion.div>
        </div>
      )}

      {/* Bid Modal */}
      {showBidModal && selectedItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <motion.div
            className="bg-white rounded-xl max-w-md w-full p-6"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Place Your Bid</h3>
              <button
                onClick={() => setShowBidModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <div className="mb-4">
              <h4 className="font-semibold text-gray-900 mb-2">{selectedItem.title}</h4>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Current Bid:</span>
                <span className="font-bold text-mini-red">
                  R{(selectedItem.current_price / 100).toFixed(2)}
                </span>
              </div>
            </div>

            <form onSubmit={handlePlaceBid} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your Bid (R)
                </label>
                <input
                  type="number"
                  step="20"
                  min={(selectedItem.current_price / 100) + 20}
                  required
                  value={bidAmount}
                  onChange={(e) => setBidAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent text-lg font-semibold"
                  placeholder="0.00"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Minimum bid: R{((selectedItem.current_price / 100) + 20).toFixed(2)} (R20 increments only)
                </p>
                <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                  <span>ℹ️</span>
                  <span>Live auction - price may change if others bid</span>
                </p>
              </div>

              <button
                type="submit"
                disabled={
                  submitting ||
                  selectedItem.status !== 'active' ||
                  new Date(selectedItem.end_date).getTime() <= Date.now()
                }
                className="w-full bg-mini-red text-white py-3 rounded-lg font-medium hover:bg-red-700 transition-colors disabled:bg-gray-300"
              >
                {submitting ? 'Placing Bid...' : 'Confirm Bid'}
              </button>
            </form>
          </motion.div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedItem && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={() => setShowDetailsModal(false)}>
          <motion.div
            className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">{selectedItem.title}</h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            {/* Image Gallery */}
            <div className="relative bg-gray-100">
              {selectedItem.image_urls && selectedItem.image_urls.length > 0 ? (
                <>
                  <img
                    src={selectedItem.image_urls[currentImageIndex]}
                    alt={`${selectedItem.title} - Image ${currentImageIndex + 1}`}
                    className="w-full h-96 object-contain"
                    loading={currentImageIndex === 0 ? 'eager' : 'lazy'}
                    decoding="async"
                  />
                  {selectedItem.image_urls.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-colors"
                      >
                        <ChevronLeft size={24} />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-colors"
                      >
                        <ChevronRight size={24} />
                      </button>
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                        {currentImageIndex + 1} / {selectedItem.image_urls.length}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-96 flex items-center justify-center">
                  <Gavel size={64} className="text-gray-400" />
                </div>
              )}
            </div>

            {/* Thumbnail Gallery */}
            {selectedItem.image_urls && selectedItem.image_urls.length > 1 && (
              <div className="px-6 py-4 flex gap-2 overflow-x-auto">
                {selectedItem.image_urls.map((url, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                      index === currentImageIndex ? 'border-mini-red' : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <img src={url} alt={`Thumbnail ${index + 1}`} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                  </button>
                ))}
              </div>
            )}

            {/* Details Content */}
            <div className="px-6 py-4 space-y-6">
              {/* Status Badge */}
              <div className="flex items-center gap-2">
                {getStatusBadge(selectedItem)}
                {selectedItem.is_featured && (
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded">
                    Featured
                  </span>
                )}
              </div>

              {/* Pricing Grid */}
              <div className={`grid ${selectedItem.status === 'sold' ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-2 md:grid-cols-4'} gap-4 p-4 bg-gray-50 rounded-lg`}>
                <div>
                  <p className="text-sm text-gray-600 mb-1">{selectedItem.status === 'sold' ? 'Sold For' : 'Current Bid'}</p>
                  <p className="text-2xl font-bold text-mini-red">R{(selectedItem.current_price / 100).toFixed(2)}</p>
                </div>
                {selectedItem.status !== 'sold' && (
                  <>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Starting Price</p>
                      <p className="text-lg font-semibold text-gray-900">R{(selectedItem.starting_price / 100).toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Total Bids</p>
                      <p className="text-lg font-semibold text-gray-900 flex items-center">
                        <TrendingUp size={16} className="mr-1" />
                        {selectedItem.bid_count || 0}
                      </p>
                    </div>
                  </>
                )}
                <div>
                  <p className="text-sm text-gray-600 mb-1">{selectedItem.status === 'sold' ? 'Sold On' : 'Time Remaining'}</p>
                  <p className="text-lg font-semibold text-gray-900 flex items-center">
                    <Clock size={16} className="mr-1" />
                    {selectedItem.status === 'sold' ? new Date(selectedItem.end_date).toLocaleDateString('en-ZA') : getTimeRemaining(selectedItem.end_date)}
                  </p>
                </div>
              </div>

              {/* Description */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Description</h3>
                <p className="text-gray-700 whitespace-pre-line">{selectedItem.description}</p>
              </div>

              {/* Item Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Category</p>
                  <p className="text-gray-900 font-medium">{formatCategoryName(selectedItem.category)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Condition</p>
                  <p className="text-gray-900 font-medium capitalize">{selectedItem.condition.replace('-', ' ')}</p>
                </div>
                {selectedItem.reserve_price && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Reserve Price</p>
                    <p className="text-gray-900 font-medium">R{(selectedItem.reserve_price / 100).toFixed(2)}</p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    handleBidClick(selectedItem);
                  }}
                  disabled={selectedItem.status !== 'active'}
                  className="flex-1 bg-mini-red text-white py-3 rounded-lg font-medium hover:bg-red-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {selectedItem.status === 'active' ? 'Place Bid' : 'Auction Ended'}
                </button>
                <button
                  onClick={() => handleShare(selectedItem)}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                >
                  <Share2 size={18} />
                  Share
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AuctionsPage;
